// See services/sosInventoryService/README.md for information about the structure
// of schema.js files.
import { Decimal, Money } from "classes/DecimalClasses";

import { i18n } from "services/i18nService";
import {
  extractName,
  formatDocumentInfo,
  formatDateTimeToDate,
  formatNumberLinkField,
  truncate,
  formatBooleanToYesOrBlank,
  extractDescription,
  formatQuantityWithUom,
  getQuantityTotal,
  formatMoneyWithAdornments,
  getMoneyTotalWithAdornments,
  formatLinkableItem,
} from "services/utility/formatting";
import {
  inventoryItemTypeEditStrFunction,
  textTypeEditStrFunction,
  decimalTypeEditStrFunction,
  referenceTypeEditStrFunction,
  arrayOfReferencesTypeEditStrFunction,
} from "services/utility/list";
import { parseHTML } from "services/utility/parsing";
import { TYPES, starColumn } from "services/utility/schema";

import { NO_REF_NUMBER_STRING } from "appConstants";
import { DEFAULT_DECIMALS_UNROUNDED } from "appConstants";

export const columnInfo = [
  starColumn,
  {
    name: "number",
    fixed: true,
    linkField: true,
    formatFunc: formatNumberLinkField,
    minWidth: "7em",
    heading: i18n("columns.ReferenceNumber"),
    sortable: true,
  },
  {
    name: "id",
    heading: i18n("columns.System"),
    sortDirection: "asc",
    minWidth: "7em",
    align: "right",
    sortable: true,
  },
  {
    name: "date",
    heading: i18n("columns.Date"),
    formatFunc: formatDateTimeToDate,
    defaultSort: true,
    sortDirection: "asc",
    sortable: true,
    default: true,
  },
  {
    name: "location",
    heading: i18n("columns.Location"),
    formatFunc: extractName,
    sortable: true,
  },
  {
    name: "comment",
    heading: i18n("columns.Comment"),
    formatFunc: [parseHTML, truncate],
    downloadFn: parseHTML,
    minWidth: "25em",
    maxWidth: "25em",
    sortable: true,
    default: true,
  },
  {
    name: "documents",
    formatFunc: formatDocumentInfo,
    heading: i18n("columns.Documents"),
    minWidth: "15em",
    default: true,
  },
  {
    name: "account",
    heading: i18n("columns.AdjustmentAccount"),
    formatFunc: extractName,
    minWidth: "12em",
    sortable: true,
  },
  {
    name: "department",
    heading: i18n("columns.Department"),
    formatFunc: extractName,
    sortable: true,
  },
  {
    name: "hasSignature",
    heading: i18n("columns.Signature"),
    formatFunc: formatBooleanToYesOrBlank,
  },
];

export function getEmptyRecord(settings) {
  return {
    starred: 0,
    syncToken: 0,
    number: "",
    date: new Date(),
    total: new Decimal(0),
    account: settings.defaultAdjustmentAccount,
    location: null,
    department: null,
    comment: null,
    customFields: null,
    archived: false,
    summaryOnly: false,
    hasSignature: false,
    documents: [],
    keys: null,
    values: null,
    lines: [],
  };
}

export const LINE_ITEM_METADATA = {
  lineNumber: {},
  item: {
    type: TYPES.reference,
    editStr: inventoryItemTypeEditStrFunction,
    initialValue: { id: null, name: null, description: null },
  },
  description: {
    type: TYPES.text,
    editStr: textTypeEditStrFunction,
  },
  class: {
    type: TYPES.reference,
    editStr: referenceTypeEditStrFunction,
  },
  jobWorkcenter: {
    type: TYPES.reference,
    editStr: referenceTypeEditStrFunction,
  },
  uom: {
    type: TYPES.reference,
    editStr: referenceTypeEditStrFunction,
  },
  serials: {
    type: TYPES.arrayOfReferences,
    editStr: arrayOfReferencesTypeEditStrFunction,
    initialValue: null,
  },
  lot: {
    type: TYPES.reference,
    editStr: textTypeEditStrFunction,
  },
  bin: { type: TYPES.reference, editStr: referenceTypeEditStrFunction },
  quantityDiff: {
    type: TYPES.decimal,
    placesRight: DEFAULT_DECIMALS_UNROUNDED,
    editStr: decimalTypeEditStrFunction,
    initialValue: new Decimal(0),
  },
  newQuantity: {
    type: TYPES.itemField,
    placesRight: DEFAULT_DECIMALS_UNROUNDED,
    editStr: decimalTypeEditStrFunction,
    initialValue: new Decimal(0),
  },
  valueDiff: {
    type: TYPES.money,
    placesRight: DEFAULT_DECIMALS_UNROUNDED,
    editStr: decimalTypeEditStrFunction,
    initialValue: new Money(0),
  },
  onhand: {
    type: TYPES.itemField,
    editStr: decimalTypeEditStrFunction,
    initialValue: new Decimal(0),
  },
  itemDetails: { initialValue: {} },
  relatedRecords: { initialValue: {} },
};

export const FIELDS_TO_TRANSFORM = {
  DECIMAL_FIELDS: ["total"],
  MONEY_FIELDS: [],
  DATE_FIELDS: [],
  DATETIME_FIELDS: ["date"],
  NON_UTC_DATETIME_FIELDS: [],
};

export const LINE_ACTIONS = [
  ["quickView", "pdf", "email"],
  "template",
  "archive",
  "delete",
  "history",
  "sync",
];
export const BATCH_ACTIONS = [["pdf", "email"], "delete", "archive", "sync"];

export const REFERENCE_FIELDS = ["location"];

export const ITEM_CALCULATED_FIELDS = ["onhand", "available"];

export const QUICK_VIEW_CONFIG = {
  tableConfig: [{ type: "lines" }],
  headerFields: [
    {
      name: "date",
      formatFunc: formatDateTimeToDate,
      label: i18n("columns.Date"),
    },
    {
      name: "number",
      label: i18n("frmLabel.AdjustmentNumber"),
      defaultValue: NO_REF_NUMBER_STRING,
    },
  ],
  fields: [
    {
      label: i18n("frmLabel.Location"),
      name: "location",
      formatFunc: extractName,
    },
  ],
  lines: [
    {
      name: "item",
      formatFunc: formatLinkableItem,
      heading: i18n("columns.Item"),
      align: "left",
      isItemField: true,
    },
    {
      name: "description",
      formatFunc: extractDescription,
      heading: i18n("columns.Description"),
      align: "left",
    },
    {
      name: "quantityDiff",
      formatFunc: formatQuantityWithUom,
      footerFunc: getQuantityTotal,
      heading: i18n("columns.QtyDiff"),
      align: "right",
    },
    {
      name: "valueDiff",
      formatFunc: formatMoneyWithAdornments,
      footerFunc: getMoneyTotalWithAdornments,
      heading: i18n("columns.ValueDiff"),
      align: "right",
    },
  ],
};
